import React, { useEffect, useState, useContext } from 'react';
import API from '../api';
import { AuthContext } from '../components/AuthContext';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import './AdminVendorsPage.css';

const AdminVendorsPage = () => {
  const { user, login } = useContext(AuthContext);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [sellerDetails, setSellerDetails] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [createdUserId, setCreatedUserId] = useState(null);
  const [newSellerUser, setNewSellerUser] = useState({ email: '', password: '' });
  const [newSellerInfo, setNewSellerInfo] = useState({
    resp_name: '', resp_last_name: '', store_name: '', store_description: '', 
    address: '', city: '', zip: '', province: '', country: '', website: '', 
    business_name: '', vat_number: '', fiscal_code: '', univocal_code: '', pec: ''
  });
  const [autoHandle, setAutoHandle] = useState('');
  const [createMsg, setCreateMsg] = useState(null);
  const [creating, setCreating] = useState(false);
  const [categoryMsg, setCategoryMsg] = useState('');
  const [importLogs, setImportLogs] = useState({});
  const [showLogs, setShowLogs] = useState({});
  const [triggeringImport, setTriggeringImport] = useState({});
  const [importStats, setImportStats] = useState(null);
  const navigate = useNavigate();

  // Fetch sellers
  useEffect(() => {
    if (!(user && user.role === 'admin')) return;
    setLoading(true); 
    setError(null);
    Promise.all([
      API.get('/seller/admin/sellers'),
      API.get('/seller/admin/import-stats')
    ])
      .then(([sellersRes, statsRes]) => {
        setSellers(sellersRes.data.sellers);
        setImportStats(statsRes.data);
      })
      .catch(() => setError('Errore nel recupero dei venditori'))
      .finally(() => setLoading(false));
  }, [user]);

  // Fetch seller details on expand
  const handleExpandSeller = async (userId) => {
    if (expandedRow && expandedRow.type === 'seller' && expandedRow.id === userId) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow({ type: 'seller', id: userId });
    if (!sellerDetails[userId]) {
      try {
        const res = await API.get(`/seller/admin/sellers/${userId}`);
        setSellerDetails(prev => ({ ...prev, [userId]: res.data.seller }));
      } catch {
        setSellerDetails(prev => ({ ...prev, [userId]: { error: 'Errore nel caricamento dettagli' } }));
      }
    }
  };

  const handleImpersonate = async (sellerId) => {
    try {
      const res = await API.post(`/auth/admin/impersonate/${sellerId}`);
      if (res.data.success) {
        login(res.data.user, res.data.token);
        navigate('/account');
      }
    } catch (err) {
      alert('Errore durante l\'impersonificazione');
    }
  };

  const handleReturnToAdmin = async () => {
    try {
      const res = await API.post('/auth/admin/return');
      if (res.data.success) {
        login(res.data.user, res.data.token);
      }
    } catch (err) {
      alert('Errore durante il ritorno all\'admin');
    }
  };

  const handleCreateSellerUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateMsg(null);
    try {
      const res = await API.post('/auth/admin/create-seller-user', newSellerUser);
      if (res.data.success) {
        setCreatedUserId(res.data.userId);
        setCreateStep(2);
        setCreateMsg(null);
      } else {
        setCreateMsg(res.data.message || 'Errore nella creazione');
      }
    } catch (err) {
      setCreateMsg(err.response?.data?.message || 'Errore nella creazione');
    }
    setCreating(false);
  };

  // Genera handle auto
  useEffect(() => {
    if (createStep === 2 && createdUserId && newSellerInfo.store_name) {
      const slugify = (str) => str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      setAutoHandle(`${createdUserId}-${slugify(newSellerInfo.store_name)}`);
    }
  }, [createStep, createdUserId, newSellerInfo.store_name]);

  const handleCreateSellerInfo = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateMsg(null);
    try {
      await API.patch(`/auth/admin/seller-info/${createdUserId}`, {
        ...newSellerInfo,
        seller_handle: autoHandle
      });
      setCreateMsg('Dati venditore salvati con successo!');
      setShowCreateForm(false);
      setCreateStep(1);
      setCreatedUserId(null);
      setNewSellerUser({ email: '', password: '' });
      setNewSellerInfo({
        resp_name: '', resp_last_name: '', store_name: '', store_description: '', 
        address: '', city: '', zip: '', province: '', country: '', website: '', 
        business_name: '', vat_number: '', univocal_code: '', pec: ''
      });
      // Aggiorna la tabella seller
      const sellersRes = await API.get('/seller/admin/sellers');
      setSellers(sellersRes.data.sellers);
    } catch (err) {
      setCreateMsg(err.response?.data?.message || 'Errore nel salvataggio dati venditore');
    }
    setCreating(false);
  };

  const handleCreateCategories = async () => {
    setCategoryMsg('');
    try {
      const res = await API.post('/categories/create-category');
      if (res.data.success) {
        setCategoryMsg('Creazione categorie avviata.');
      } else {
        setCategoryMsg(res.data.message);
      }
    } catch (error) {
      setCategoryMsg(error.response?.data?.message || 'Errore di rete o server');
    }
  };

  // Funzioni per la gestione degli import
  const handleShowLogs = async (userId) => {
    if (showLogs[userId]) {
      setShowLogs(prev => ({ ...prev, [userId]: false }));
      if (expandedRow && expandedRow.type === 'seller' && expandedRow.id === userId) {
        setExpandedRow(null);
      }
      return;
    }
    
    try {
      const res = await API.get(`/seller/admin/sellers/${userId}/logs`);
      setImportLogs(prev => ({ ...prev, [userId]: res.data.logs }));
      setShowLogs(prev => ({ ...prev, [userId]: true }));
      
      if (!expandedRow || expandedRow.type !== 'seller' || expandedRow.id !== userId) {
        setExpandedRow({ type: 'seller', id: userId });
        if (!sellerDetails[userId]) {
          try {
            const detailsRes = await API.get(`/seller/admin/sellers/${userId}`);
            setSellerDetails(prev => ({ ...prev, [userId]: detailsRes.data.seller }));
          } catch (error) {
            setSellerDetails(prev => ({ ...prev, [userId]: { error: 'Errore nel caricamento dettagli' } }));
          }
        }
      }
    } catch (error) {
      console.error('Errore nel recupero dei log:', error);
      setImportLogs(prev => ({ ...prev, [userId]: 'Errore nel caricamento dei log' }));
      setShowLogs(prev => ({ ...prev, [userId]: true }));
    }
  };

  const handleTriggerImport = async (userId) => {
    setTriggeringImport(prev => ({ ...prev, [userId]: true }));
    try {
      const res = await API.post(`/seller/admin/sellers/${userId}/trigger-import`);
      if (res.data.success) {
        alert('Import avviato manualmente!');
        const sellersRes = await API.get('/seller/admin/sellers');
        setSellers(sellersRes.data.sellers);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Errore nell\'avvio dell\'import');
    } finally {
      setTriggeringImport(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Funzioni di utilità
  const formatImportDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A';
    const start = new Date(startTime);
    const end = new Date(endTime);
    const duration = end - start;
    const minutes = Math.floor(duration / (1000 * 60));
    const seconds = Math.floor((duration % (1000 * 60)) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Mai';
    const now = new Date();
    const time = new Date(timestamp);
    const diff = now - time;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) {
      return `${hours}h ${minutes}m fa`;
    } else {
      return `${minutes}m fa`;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_progress': return '#007bff';
      case 'completed': return '#28a745';
      case 'error': return '#dc3545';
      case 'idle': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'in_progress': return 'In corso';
      case 'completed': return 'Completato';
      case 'error': return 'Errore';
      case 'idle': return 'In attesa';
      default: return 'Sconosciuto';
    }
  };

  return (
    <AdminLayout>
      <div className="admin-vendors-header">
        <h2>Gestione Venditori</h2>
      </div>

      <div className="admin-vendors-actions">
        <div className="admin-vendors-actions-row">
          <button
            onClick={() => setShowCreateForm(v => !v)}
            className="admin-action-btn"
          >{showCreateForm ? 'Annulla' : 'Crea nuovo venditore'}</button>
          <button
            onClick={handleCreateCategories}
            className="admin-action-btn"
          >Crea categorie</button>
        </div>
        {categoryMsg && <div className="admin-vendors-category-msg">{categoryMsg}</div>}
        
        {showCreateForm && (
          <div className="admin-vendors-create-form">
            {createStep === 1 ? (
              <form onSubmit={handleCreateSellerUser} className="admin-vendors-form-row">
                <div className="admin-vendors-form-group">
                  <label className="admin-vendors-form-label">Email venditore</label>
                  <input type="email" value={newSellerUser.email} onChange={e => setNewSellerUser(s => ({ ...s, email: e.target.value }))} required className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group small">
                  <label className="admin-vendors-form-label">Password temporanea</label>
                  <input type="password" value={newSellerUser.password} onChange={e => setNewSellerUser(s => ({ ...s, password: e.target.value }))} required className="admin-vendors-form-input" />
                </div>
                <button type="submit" className="admin-action-btn admin-vendors-form-submit" disabled={creating}>
                  {creating ? 'Creazione...' : 'Crea'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleCreateSellerInfo} className="admin-vendors-form-row">
                <div className="admin-vendors-form-group small">
                  <label className="admin-vendors-form-label">Nome referente</label>
                  <input value={newSellerInfo.resp_name} onChange={e => setNewSellerInfo(s => ({ ...s, resp_name: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group small">
                  <label className="admin-vendors-form-label">Cognome referente</label>
                  <input value={newSellerInfo.resp_last_name} onChange={e => setNewSellerInfo(s => ({ ...s, resp_last_name: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group small">
                  <label className="admin-vendors-form-label">Nome negozio</label>
                  <input value={newSellerInfo.store_name} onChange={e => setNewSellerInfo(s => ({ ...s, store_name: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group small">
                  <label className="admin-vendors-form-label">Handle negozio (auto)</label>
                  <input value={autoHandle} disabled className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group large">
                  <label className="admin-vendors-form-label">Descrizione negozio</label>
                  <input value={newSellerInfo.store_description} onChange={e => setNewSellerInfo(s => ({ ...s, store_description: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group extra-large">
                  <label className="admin-vendors-form-label">Indirizzo</label>
                  <input value={newSellerInfo.address} onChange={e => setNewSellerInfo(s => ({ ...s, address: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group medium">
                  <label className="admin-vendors-form-label">Città</label>
                  <input value={newSellerInfo.city} onChange={e => setNewSellerInfo(s => ({ ...s, city: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group medium">
                  <label className="admin-vendors-form-label">CAP</label>
                  <input value={newSellerInfo.zip} onChange={e => setNewSellerInfo(s => ({ ...s, zip: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group medium">
                  <label className="admin-vendors-form-label">Provincia</label>
                  <input value={newSellerInfo.province} onChange={e => setNewSellerInfo(s => ({ ...s, province: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group medium">
                  <label className="admin-vendors-form-label">Nazione</label>
                  <input value={newSellerInfo.country} onChange={e => setNewSellerInfo(s => ({ ...s, country: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group extra-large">
                  <label className="admin-vendors-form-label">Sito web</label>
                  <input value={newSellerInfo.website} onChange={e => setNewSellerInfo(s => ({ ...s, website: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group extra-large">
                  <label className="admin-vendors-form-label">Ragione sociale</label>
                  <input value={newSellerInfo.business_name} onChange={e => setNewSellerInfo(s => ({ ...s, business_name: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group medium">
                  <label className="admin-vendors-form-label">Partita IVA</label>
                  <input value={newSellerInfo.vat_number} onChange={e => setNewSellerInfo(s => ({ ...s, vat_number: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group medium">
                  <label className="admin-vendors-form-label">Codice Fiscale</label>
                  <input value={newSellerInfo.fiscal_code} onChange={e => setNewSellerInfo(s => ({ ...s, fiscal_code: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group medium">
                  <label className="admin-vendors-form-label">Codice univoco</label>
                  <input value={newSellerInfo.univocal_code} onChange={e => setNewSellerInfo(s => ({ ...s, univocal_code: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <div className="admin-vendors-form-group extra-large">
                  <label className="admin-vendors-form-label">PEC</label>
                  <input value={newSellerInfo.pec} onChange={e => setNewSellerInfo(s => ({ ...s, pec: e.target.value }))} className="admin-vendors-form-input" />
                </div>
                <button type="submit" className="admin-action-btn admin-vendors-form-submit" disabled={creating}>
                  {creating ? 'Salvataggio...' : 'Salva'}
                </button>
              </form>
            )}
          </div>
        )}
        {createMsg && <div className={`admin-vendors-create-msg ${createMsg.includes('successo') ? 'success' : ''}`}>{createMsg}</div>}
      </div>

      {user.impersonatedBy && (
        <div className="admin-vendors-impersonation">
          <b>Stai impersonificando un venditore.</b>
          <button onClick={handleReturnToAdmin} className="admin-return-btn">Torna all'admin</button>
        </div>
      )}
      
      {/* Statistiche Import */}
      {importStats && (
        <div className="admin-vendors-stats">
          <h4 className="admin-vendors-stats-title">Statistiche Import</h4>
          <div className="admin-vendors-stats-grid">
            <div className="admin-vendors-stat-item">
              <div className="admin-vendors-stat-value">
                {importStats.stats.total_sellers}
              </div>
              <div className="admin-vendors-stat-label">Venditori Totali</div>
            </div>
            <div className="admin-vendors-stat-item">
              <div className="admin-vendors-stat-value">
                {importStats.stats.in_progress}
              </div>
              <div className="admin-vendors-stat-label">In Corso</div>
            </div>
            <div className="admin-vendors-stat-item">
              <div className="admin-vendors-stat-value success">
                {importStats.stats.completed}
              </div>
              <div className="admin-vendors-stat-label">Completati</div>
            </div>
            <div className="admin-vendors-stat-item">
              <div className="admin-vendors-stat-value error">
                {importStats.stats.error}
              </div>
              <div className="admin-vendors-stat-label">Errori</div>
            </div>
            <div className="admin-vendors-stat-item">
              <div className="admin-vendors-stat-value muted">
                {importStats.stats.idle}
              </div>
              <div className="admin-vendors-stat-label">In Attesa</div>
            </div>
            <div className="admin-vendors-stat-item">
              <div className="admin-vendors-stat-value info">
                {importStats.stats.avg_duration_seconds ? 
                  Math.round(importStats.stats.avg_duration_seconds / 60) : 'N/A'}
              </div>
              <div className="admin-vendors-stat-label">Durata Media (min)</div>
            </div>
            <div className="admin-vendors-stat-item">
              <div className="admin-vendors-stat-value products">
                {importStats.stats.total_products || 0}
              </div>
              <div className="admin-vendors-stat-label">Totale Prodotti</div>
            </div>
          </div>
          
          {importStats.recentImports && importStats.recentImports.length > 0 && (
            <div className="admin-vendors-recent-imports">
              <h5 className="admin-vendors-recent-title">Ultimi Import</h5>
              <div className="admin-vendors-recent-list">
                {importStats.recentImports.slice(0, 3).map((import_, index) => (
                  <div key={index} className="admin-vendors-recent-item">
                    <strong>{import_.store_name}</strong> - {getStatusText(import_.import_status)} 
                    {import_.duration_seconds && ` (${Math.round(import_.duration_seconds / 60)}m ${import_.duration_seconds % 60}s)`}
                    {import_.import_error && ` - Errore: ${import_.import_error.substring(0, 50)}...`}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="admin-vendors-loading">Caricamento...</div>
      ) : error ? (
        <div className="admin-vendors-error">{error}</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Logo</th>
              <th>Nome negozio</th>
              <th>Integrazione</th>
              <th>Status</th>
              <th>Import</th>
              <th>Durata</th>
              <th>Prodotti</th>
              <th>Tar. Sped.</th>
              <th>Merchant</th>
              <th></th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {sellers.map(seller => (
              <React.Fragment key={seller.user_id}>
                <tr
                  className={expandedRow && expandedRow.type === 'seller' && expandedRow.id === seller.user_id ? 'expanded' : ''}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleExpandSeller(seller.user_id)}
                >
                  <td>{seller.user_id}</td>
                  <td>{seller.store_logo_square ? <img src={seller.store_logo_square} alt="logo" className="admin-vendors-logo" /> : '-'}</td>
                  <td>{seller.store_name}</td>
                  <td>{seller.integration_method || 'N/A'}</td>
                  <td>
                    <span className={`admin-vendors-status-badge ${seller.import_status}`}>
                      {getStatusText(seller.import_status)}
                    </span>
                    {seller.import_error && (
                      <div className="admin-vendors-error-text">
                        Errore: {seller.import_error.substring(0, 30)}...
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="admin-vendors-time">
                      <div>{formatTimeAgo(seller.last_import_end)}</div>
                    </div>
                  </td>
                  <td>
                    {formatImportDuration(seller.last_import_start, seller.last_import_end)}
                  </td>
                  <td>
                    <span className={`admin-vendors-products-badge ${seller.products_count > 0 ? 'has-products' : 'no-products'}`}>
                      {seller.products_count || 0}
                    </span>
                  </td>
                  <td>
                    <span className={`admin-vendors-shipping-badge ${seller.shipping_rates_count === 0 ? 'empty' : ''}`}>
                      {seller.shipping_rates_count || 0}
                    </span>
                  </td>
                  <td>
                    {seller.merchant_info ? (
                      <div className="admin-vendors-merchant-info">
                        <div className={`admin-vendors-merchant-status ${seller.merchant_info.merchant_status}`}>
                          {seller.merchant_info.merchant_status === 'active' ? 'Attivo' :
                           seller.merchant_info.merchant_status === 'pending' ? 'In Attesa' : 'Sospeso'}
                        </div>
                        <div className="admin-vendors-merchant-id">
                          {seller.merchant_info.merchant_id}
                        </div>
                      </div>
                    ) : (
                      <span className="admin-vendors-merchant-not-created">Non creato</span>
                    )}
                  </td>
                  <td>
                    {seller.seller_handle ? (
                      <a
                        href={`/seller/${seller.seller_handle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-primary btn-sm"
                        onClick={e => e.stopPropagation()}
                      >Vedi</a>
                    ) : '-'}
                  </td>
                  <td>
                    <div className="admin-vendors-actions-column">
                      <button
                        onClick={e => { e.stopPropagation(); handleImpersonate(seller.user_id); }}
                        disabled={user.impersonatedBy}
                        className="admin-action-btn admin-vendors-action-btn"
                      >Accedi</button>
                      <button
                        onClick={e => { e.stopPropagation(); handleShowLogs(seller.user_id); }}
                        className="admin-action-btn admin-vendors-action-btn logs"
                      >
                        {showLogs[seller.user_id] ? 'Nascondi Log' : 'Mostra Log'}
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); handleTriggerImport(seller.user_id); }}
                        disabled={triggeringImport[seller.user_id] || seller.import_status === 'in_progress'}
                        className={`admin-action-btn admin-vendors-action-btn import ${triggeringImport[seller.user_id] ? 'disabled' : ''}`}
                      >
                        {triggeringImport[seller.user_id] ? 'Avvio...' : 'Avvia Import'}
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedRow && expandedRow.type === 'seller' && expandedRow.id === seller.user_id && (
                  <tr>
                    <td colSpan={13} className="expand-row">
                      <div className="admin-vendors-expanded-content">
                        {/* Dettagli Venditore */}
                        <div className="admin-vendors-details-section">
                          <h4 className="admin-vendors-details-title">Dettagli Venditore</h4>
                          {sellerDetails[seller.user_id] ? (
                            sellerDetails[seller.user_id].error ? (
                              <div style={{ color: 'red' }}>{sellerDetails[seller.user_id].error}</div>
                            ) : (
                              <div className="admin-vendors-details-content">
                                {Object.entries(sellerDetails[seller.user_id])
                                  .filter(([k, v]) => v !== null && v !== '')
                                  .map(([k, v]) => (
                                    <div key={k} className="admin-vendors-details-item">
                                      <strong className="admin-vendors-details-label">{k}:</strong> 
                                      <span className="admin-vendors-details-value">
                                        {typeof v === 'string' && v.length > 100 
                                          ? `${v.substring(0, 100)}...` 
                                          : v}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            )
                          ) : (
                            <div>Caricamento dettagli...</div>
                          )}
                        </div>

                        {/* Informazioni Tariffe di Spedizione */}
                        <div className="admin-vendors-shipping-section">
                          <h4 className="admin-vendors-shipping-title">Tariffe di Spedizione</h4>
                          <div className="admin-vendors-shipping-content">
                            <div className="admin-vendors-shipping-item">
                              <strong className="admin-vendors-shipping-label">Numero tariffe configurate:</strong>
                              <span className={`admin-vendors-shipping-count ${seller.shipping_rates_count === 0 ? 'empty' : ''}`}>
                                {seller.shipping_rates_count || 0}
                              </span>
                            </div>
                            {seller.shipping_rates_count === 0 && (
                              <div className="admin-vendors-shipping-warning">
                                Nessuna tariffa di spedizione configurata
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Informazioni Prodotti */}
                        <div className="admin-vendors-products-section">
                          <h4 className="admin-vendors-products-title">Prodotti</h4>
                          <div className="admin-vendors-products-content">
                            <div className="admin-vendors-products-item">
                              <strong className="admin-vendors-products-label">Numero prodotti importati:</strong>
                              <span className={`admin-vendors-products-count ${seller.products_count === 0 ? 'empty' : ''}`}>
                                {seller.products_count || 0}
                              </span>
                            </div>
                            {seller.products_count === 0 && (
                              <div className="admin-vendors-products-warning">
                                Nessun prodotto importato
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Informazioni Merchant Account */}
                        <div className="admin-vendors-merchant-section">
                          <h4 className="admin-vendors-merchant-title">Merchant Account</h4>
                          <div className="admin-vendors-merchant-content">
                            {seller.merchant_info ? (
                              <>
                                <div className="admin-vendors-merchant-item">
                                  <strong className="admin-vendors-merchant-label">Stato:</strong>
                                  <span className={`admin-vendors-merchant-badge ${seller.merchant_info.merchant_status === 'active' ? '' : 'error'}`}>
                                    {seller.merchant_info.merchant_status === 'active' ? 'Attivo' :
                                     seller.merchant_info.merchant_status === 'pending' ? 'In Attesa' : 'Sospeso'}
                                  </span>
                                </div>
                                <div className="admin-vendors-merchant-item">
                                  <strong className="admin-vendors-merchant-label">Merchant ID:</strong>
                                  <span className="admin-vendors-merchant-value">
                                    {seller.merchant_info.merchant_id}
                                  </span>
                                </div>
                                <div className="admin-vendors-merchant-item">
                                  <strong className="admin-vendors-merchant-label">Nome Account:</strong>
                                  <span className="admin-vendors-merchant-value">
                                    {seller.merchant_info.merchant_name}
                                  </span>
                                </div>
                                <div className="admin-vendors-merchant-item">
                                  <strong className="admin-vendors-merchant-label">Autorizzazioni Seller:</strong>
                                  <span className={`admin-vendors-merchant-badge ${seller.merchant_info.seller_permissions_granted ? '' : 'error'}`}>
                                    {seller.merchant_info.seller_permissions_granted ? 'Concesse' : 'Non Concesse'}
                                  </span>
                                </div>
                                {seller.merchant_info.merchant_created_at && (
                                  <div className="admin-vendors-merchant-item">
                                    <strong className="admin-vendors-merchant-label">Data Creazione:</strong>
                                    <span className="admin-vendors-merchant-value">
                                      {new Date(seller.merchant_info.merchant_created_at).toLocaleDateString('it-IT')}
                                    </span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="admin-vendors-merchant-warning">
                                Nessun merchant account creato per questo venditore
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Log di Import */}
                        {showLogs[seller.user_id] && (
                          <div className="admin-vendors-logs-section">
                            <h4 className="admin-vendors-logs-title">Log di Import</h4>
                            <pre className="admin-vendors-logs-content">
                              {importLogs[seller.user_id] || 'Nessun log disponibile'}
                            </pre>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </AdminLayout>
  );
};

export default AdminVendorsPage; 